import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Map, Flame, ShoppingBag, CalendarDays, Compass } from 'lucide-react';
import ItineraryView from './ItineraryView';
import FuellingView from './FuellingView';
import ShoppingView from './ShoppingView';
import RaceWeekView, { RACE_WEEK_TAB_META, raceWeekTabs, raceWeekTabSub, type RaceWeekTab } from './RaceWeekView';
import type { EventGuide, Race } from './types';

export type EventSection = 'itinerary' | 'fuelling' | 'shopping' | 'race-week';

export const EVENT_SECTIONS: EventSection[] = ['itinerary', 'fuelling', 'shopping', 'race-week'];

const sectionMeta: Record<EventSection, { label: string; sub: string; icon: React.ReactNode }> = {
  itinerary: { label: 'Itinerary', sub: 'Day by day', icon: <Map size={18} /> },
  fuelling: { label: 'Fuelling', sub: 'Carb load', icon: <Flame size={18} /> },
  shopping: { label: 'Shopping', sub: 'Groceries', icon: <ShoppingBag size={18} /> },
  'race-week': { label: 'Race Week', sub: 'Events & perks', icon: <CalendarDays size={18} /> },
};

/** Sections this race can show. Fuelling always works — the planner only needs a date and start time. */
export const availableSections = (guide?: EventGuide): EventSection[] =>
  EVENT_SECTIONS.filter((s) => {
    if (s === 'fuelling') return true;
    if (s === 'itinerary') return !!guide?.itinerary;
    if (s === 'shopping') return !!guide?.shopping;
    return !!guide?.raceWeek;
  });

const fallbackLabel = (race: Race) => `${race.city} ${race.date.slice(0, 4)}`;

/** Race Week sub-tab from the hash's fourth segment, e.g. #race/chicago-2026/race-week/calendar. */
const tabFromHash = (): RaceWeekTab => {
  const sub = window.location.hash.split('/')[3];
  return sub === 'calendar' || sub === 'spectators' ? sub : 'perks';
};

const EventHub: React.FC<{
  race: Race;
  guide?: EventGuide;
  section: EventSection;
  onSectionChange: (section: EventSection) => void;
}> = ({ race, guide, section, onSectionChange }) => {
  const sections = availableSections(guide);
  const active = sections.includes(section) ? section : sections[0];
  const label = guide?.label ?? fallbackLabel(race);
  const gunLabel = guide?.gunLabel ?? `${race.date} · ${race.startTime} local`;
  const rwTabs = guide?.raceWeek ? raceWeekTabs(guide.raceWeek) : [];
  const [rwTab, setRwTab] = useState<RaceWeekTab>(tabFromHash);

  useEffect(() => {
    const onHashChange = () => setRwTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [active, rwTab]);

  const selectRwTab = (tab: RaceWeekTab) => {
    setRwTab(tab);
    // replaceState keeps the tab linkable without a hashchange round-trip through App routing.
    const base = `#race/${race.id}/race-week`;
    window.history.replaceState(null, '', tab === 'perks' ? base : `${base}/${tab}`);
  };

  return (
    <div className={`event-hub theme-${guide?.theme ?? 'generic'}`}>
      <nav className="ev-subnav" aria-label={`${race.city} sections`}>
        <div className="ev-subnav-inner" style={{ gridTemplateColumns: `repeat(${sections.length}, 1fr)` }}>
          {sections.map((id) => {
            const item = sectionMeta[id];
            const isActive = active === id;
            return (
              <button
                key={id}
                className={`ev-subnav-btn ${isActive ? 'ev-subnav-btn--active' : ''}`}
                onClick={() => onSectionChange(id)}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="ev-subnav-icon">{item.icon}</span>
                <span className="ev-subnav-label">
                  <span className="ev-subnav-title">{item.label}</span>
                  <span className="ev-subnav-sub">{item.sub}</span>
                </span>
                {isActive && (
                  <motion.span
                    layoutId="ev-subnav-underline"
                    className="ev-subnav-underline"
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                  />
                )}
              </button>
            );
          })}
        </div>
        {active === 'race-week' && rwTabs.length > 1 && (
          <div
            className="ev-subnav-inner ev-subnav-inner--second"
            role="tablist"
            aria-label="Race week"
            style={{ gridTemplateColumns: `repeat(${rwTabs.length}, 1fr)` }}
          >
            {rwTabs.map((t) => {
              const isActive = rwTab === t;
              return (
                <button
                  key={t}
                  role="tab"
                  aria-selected={isActive}
                  className={`ev-subnav-btn ${isActive ? 'ev-subnav-btn--active' : ''}`}
                  onClick={() => selectRwTab(t)}
                >
                  <span className="ev-subnav-icon">{RACE_WEEK_TAB_META[t].icon}</span>
                  <span className="ev-subnav-label">
                    <span className="ev-subnav-title">{RACE_WEEK_TAB_META[t].label}</span>
                    <span className="ev-subnav-sub">{raceWeekTabSub(t, guide!.raceWeek!)}</span>
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="ev-subnav-underline-second"
                      className="ev-subnav-underline"
                      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {!guide && (
        <section className="ev-section">
          <div className="guide-pending">
            <Compass size={18} />
            <p>
              <strong>The full {race.city} guide isn{"'"}t written yet.</strong> The carb-load planner and race-morning
              timeline below already work for {race.name}. Itinerary, shopping list, and race-week events arrive once
              the city has been researched.
            </p>
          </div>
        </section>
      )}

      {active === 'itinerary' && guide?.itinerary ? (
        <ItineraryView itinerary={guide.itinerary} theme={guide.theme} accent={guide.accent} />
      ) : active === 'shopping' && guide?.shopping ? (
        <ShoppingView
          race={race}
          shopping={guide.shopping}
          label={label}
          gunLabel={gunLabel}
          storageKey={`shopping-checked:${race.id}`}
        />
      ) : active === 'race-week' && guide?.raceWeek ? (
        <RaceWeekView
          race={race}
          raceWeek={guide.raceWeek}
          label={label}
          waves={guide.fuelling?.waves}
          tab={rwTab}
        />
      ) : (
        <FuellingView race={race} plan={guide?.fuelling} label={label} gunLabel={gunLabel} />
      )}
    </div>
  );
};

export default EventHub;
